import { useRef, useState } from "react";
import { Paperclip, Send, Loader2, Aperture } from "lucide-react";
import { toast } from "sonner";
import { useVault } from "@/context/VaultContext";
import { SnapDialog } from "@/components/aph/SnapDialog";

const MAX = 10 * 1024 * 1024;

export function Composer({ sid }) {
  const { sendMessage } = useVault();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const fileRef = useRef(null);

  const send = async (payload) => {
    setBusy(true);
    try {
      await sendMessage(sid, payload);
    } catch (e) {
      toast.error(e.message || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const sendText = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    await send({ text: t });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX) return toast.error("Files are limited to 10 MB");
    await send({ file });
  };

  return (
    <footer className="p-3 sm:p-4 bg-void border-t border-glass pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="flex items-end bg-nebula border border-rim rounded-secure p-1 focus-within:border-stellar/50 transition-colors">
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="composer-attach-btn" className="p-2 text-ash/50 hover:text-stellar transition-colors"><Paperclip size={20} strokeWidth={1.5} /></button>
        <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="composer-file-input" />
        <button onClick={() => setSnapOpen(true)} disabled={busy} data-testid="composer-snap-btn" title="View-once Snap" className="p-2 text-ash/50 hover:text-flare transition-colors"><Aperture size={20} strokeWidth={1.5} /></button>
        <SnapDialog open={snapOpen} onOpenChange={setSnapOpen} sid={sid} />
        <textarea data-testid="composer-input" rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Secure message…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
          className="flex-1 bg-transparent text-sm text-ash placeholder-ash/40 focus:outline-none px-2 py-2 resize-none max-h-32" />
        <button onClick={sendText} disabled={busy || !text.trim()} data-testid="composer-send-btn" className="p-2 bg-stellar rounded-secure text-void hover:opacity-80 transition-opacity disabled:opacity-40">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} strokeWidth={1.5} />}
        </button>
      </div>
    </footer>
  );
}
